import React, { useState } from 'react';
import { useWizard } from '../../contexts/WizardContext';

const Step2Services: React.FC = () => {
  const { wizardData, updateStepData, nextStep, previousStep, isLoading } = useWizard();

  const [formData, setFormData] = useState({
    services_offered: wizardData.services_offered || [],
    treatment_categories: wizardData.treatment_categories || [],
    virtual_consultations: wizardData.virtual_consultations || false,
    insurance_accepted: wizardData.insurance_accepted || [],
  });

  const [serviceInput, setServiceInput] = useState('');
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});

  const treatmentCategoryOptions = [
    'Injectables (Neurotoxin, Fillers)',
    'Laser Treatments',
    'Chemical Peels',
    'Microneedling',
    'Body Contouring',
    'Skin Rejuvenation',
    'Hair Restoration',
    'Medical Grade Skincare',
    'Wellness Treatments',
    'Other',
  ];

  const addService = (service: string) => {
    if (service && !formData.services_offered.includes(service)) {
      setFormData((prev) => ({
        ...prev,
        services_offered: [...prev.services_offered, service],
      }));
    }
    setServiceInput('');
  };

  const removeService = (service: string) => {
    setFormData((prev) => ({
      ...prev,
      services_offered: prev.services_offered.filter((s) => s !== service),
    }));
  };

  const toggleTreatmentCategory = (category: string) => {
    setFormData((prev) => ({
      ...prev,
      treatment_categories: prev.treatment_categories.includes(category)
        ? prev.treatment_categories.filter((c) => c !== category)
        : [...prev.treatment_categories, category],
    }));
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (formData.services_offered.length === 0) {
      errors.services = 'At least one service is required';
    }

    if (formData.treatment_categories.length === 0) {
      errors.categories = 'At least one treatment category is required';
    }

    setLocalErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleNext = async () => {
    if (validateForm()) {
      await updateStepData(formData);
      nextStep();
    }
  };

  // Keep unsaved edits when navigating back
  const handleBack = async () => {
    await updateStepData(formData);
    previousStep();
  };

  return (
    <div className="max-w-3xl mx-auto">
      <h2 className="font-['Ovo',_serif] text-[24px] sm:text-[28px] lg:text-[32px] text-[#1f1f1f] mb-6 sm:mb-8">Services & Treatments</h2>

      <div className="space-y-4 sm:space-y-6">
        {/* Services Offered */}
        <div>
          <label className="font-['Ovo',_serif] font-normal text-[#1f1f1f] text-[14px] sm:text-[16px] block mb-2 sm:mb-3">
            Services Offered *
          </label>
          <div className="flex flex-col sm:flex-row gap-2 mb-3">
            <input
              type="text"
              value={serviceInput}
              onChange={(e) => setServiceInput(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addService(serviceInput);
                }
              }}
              placeholder="Add a service (e.g., Neurotoxin injections, Lip fillers)"
              className="flex-1 h-12 sm:h-14 border border-[#eaeaea] px-4 sm:px-6 py-3 sm:py-4 font-['Ovo',_serif] text-[14px] sm:text-[16px] text-[#1f1f1f] placeholder:text-[#767472]"
            />
            <button
              type="button"
              onClick={() => addService(serviceInput)}
              className="px-6 h-12 sm:h-14 min-h-[44px] bg-[#c6a664] text-white font-['Ovo',_serif] hover:bg-[#b89654] transition-colors"
            >
              Add
            </button>
          </div>

          {/* Selected Services */}
          {formData.services_offered.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {formData.services_offered.map((service) => (
                <div
                  key={service}
                  className="px-4 py-2 bg-[#f5f5f5] text-[14px] font-['Ovo',_serif] flex items-center gap-2"
                >
                  {service}
                  <button
                    type="button"
                    onClick={() => removeService(service)}
                    className="text-[#767472] hover:text-[#1f1f1f]"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {localErrors.services && (
            <span className="text-red-500 text-[14px] mt-1 block">{localErrors.services}</span>
          )}
        </div>

        {/* Treatment Categories */}
        <div>
          <label className="font-['Ovo',_serif] font-normal text-[#1f1f1f] text-[14px] sm:text-[16px] block mb-2 sm:mb-3">
            Treatment Categories *
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {treatmentCategoryOptions.map((category) => (
              <label key={category} className="flex items-center gap-3 cursor-pointer min-h-[44px] py-1">
                <input
                  type="checkbox"
                  checked={formData.treatment_categories.includes(category)}
                  onChange={() => toggleTreatmentCategory(category)}
                  className="w-5 h-5 border-2 border-[#eaeaea] rounded checked:bg-[#c6a664] checked:border-[#c6a664] flex-shrink-0"
                />
                <span className="font-['Ovo',_serif] text-[14px] sm:text-[16px] text-[#1f1f1f]">{category}</span>
              </label>
            ))}
          </div>

          {localErrors.categories && (
            <span className="text-red-500 text-[14px] mt-1 block">{localErrors.categories}</span>
          )}
        </div>

        {/* Virtual Consultations */}
        <div>
          <label className="flex items-center gap-3 cursor-pointer min-h-[44px]">
            <input
              type="checkbox"
              checked={formData.virtual_consultations}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, virtual_consultations: e.target.checked }))
              }
              className="w-5 h-5 border-2 border-[#eaeaea] rounded checked:bg-[#c6a664] checked:border-[#c6a664] flex-shrink-0"
            />
            <span className="font-['Ovo',_serif] text-[14px] sm:text-[16px] text-[#1f1f1f]">
              I offer virtual consultations
            </span>
          </label>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row justify-between gap-3 sm:gap-0">
        <button
          onClick={handleBack}
          className="order-2 sm:order-1 px-6 sm:px-8 py-3 min-h-[44px] border border-[#1f1f1f] text-[#1f1f1f] font-['Ovo',_serif] text-[16px] sm:text-[18px] uppercase hover:bg-[#f5f5f5] transition-colors"
        >
          Back
        </button>
        <button
          onClick={handleNext}
          disabled={isLoading}
          className="order-1 sm:order-2 px-6 sm:px-8 py-3 min-h-[44px] bg-[#1f1f1f] text-white font-['Ovo',_serif] text-[16px] sm:text-[18px] uppercase hover:bg-[#2f2f2f] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading ? 'Saving...' : 'Next'}
        </button>
      </div>
    </div>
  );
};

export default Step2Services;
